import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { restaurar, restaurarHijos } from '../utils/softDelete';

const DIAS_PURGA = 30;

const formatFecha = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const diasRestantes = (iso) => {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso).getTime() + DIAS_PURGA * 86400000 - Date.now()) / 86400000);
  return Math.max(0, diff);
};

const BadgeDias = ({ iso }) => {
  const d = diasRestantes(iso);
  if (d === null) return null;
  const color = d <= 3 ? '#dc2626' : d <= 10 ? '#d97706' : '#6b7280';
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color, background: `${color}18`, padding: '2px 7px', borderRadius: 10, flexShrink: 0 }}>
      {d === 0 ? 'Se elimina hoy' : `${d}d restantes`}
    </span>
  );
};

function SeccionPapelera({ titulo, items, onRestaurar, onEliminar, renderItem }) {
  const [abierto, setAbierto] = useState(true);
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setAbierto(v => !v)}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{titulo}</span>
        <span style={{ fontSize: 11, color: 'var(--text4)', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '1px 7px' }}>{items.length}</span>
        <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 'auto' }}>{abierto ? '▲' : '▼'}</span>
      </div>
      {abierto && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(item => (
            <div
              key={item.id}
              style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderItem(item)}
              </div>
              <BadgeDias iso={item.eliminado_en} />
              <button
                className="btn btn-ghost btn-sm"
                style={{ flexShrink: 0, color: '#2563eb', fontWeight: 600, fontSize: 12 }}
                title="Restaurar"
                onClick={() => onRestaurar(item)}
              >
                Restaurar
              </button>
              <button
                className="btn btn-ghost btn-sm"
                style={{ flexShrink: 0, color: '#dc2626', fontWeight: 600, fontSize: 12 }}
                title="Eliminar permanentemente"
                onClick={() => onEliminar(item)}
              >
                Borrar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Papelera() {
  const [datos, setDatos] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const showToast = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 3000); };

  const purgarAntiguos = useCallback(async () => {
    const limite = new Date(Date.now() - DIAS_PURGA * 86400000).toISOString();

    // Leaf tables with eliminado
    await supabase.from('prueba_eventos').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('tareas_notas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('agenda_notas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('clientes_notas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('tramites_notas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('expedientes_notas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('movimientos').delete().eq('eliminado', true).lt('eliminado_en', limite);

    // expediente_pruebas eliminadas individualmente
    const { data: pruebas } = await supabase.from('expediente_pruebas').select('id').eq('eliminado', true).lt('eliminado_en', limite);
    if (pruebas?.length) {
      await supabase.from('prueba_eventos').delete().in('prueba_id', pruebas.map(p => p.id));
      await supabase.from('expediente_pruebas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    }

    // tareas eliminadas individualmente
    const { data: tareasDel } = await supabase.from('tareas').select('id').eq('eliminado', true).lt('eliminado_en', limite);
    if (tareasDel?.length) {
      await supabase.from('tareas_notas').delete().in('tarea_id', tareasDel.map(t => t.id));
      await supabase.from('tareas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    }

    // agenda eliminada individualmente
    await supabase.from('agenda').delete().eq('eliminado', true).lt('eliminado_en', limite);

    // expedientes y sus cascadas
    const { data: expsDel } = await supabase.from('expedientes').select('id').eq('eliminado', true).lt('eliminado_en', limite);
    if (expsDel?.length) {
      const expIds = expsDel.map(e => e.id);
      const { data: expTareas } = await supabase.from('tareas').select('id').in('expediente_id', expIds);
      if (expTareas?.length) {
        await supabase.from('tareas_notas').delete().in('tarea_id', expTareas.map(t => t.id));
        await supabase.from('tareas').delete().in('expediente_id', expIds);
      }
      const { data: expPruebas } = await supabase.from('expediente_pruebas').select('id').in('expediente_id', expIds);
      if (expPruebas?.length) {
        await supabase.from('prueba_eventos').delete().in('prueba_id', expPruebas.map(p => p.id));
        await supabase.from('expediente_pruebas').delete().in('expediente_id', expIds);
      }
      await supabase.from('expedientes_notas').delete().in('expediente_id', expIds);
      await supabase.from('movimientos').delete().in('expediente_id', expIds);
      await supabase.from('agenda').delete().in('expediente_id', expIds);
      await supabase.from('expedientes').delete().in('id', expIds);
    }

    // tramites y sus cascadas
    const { data: tramsDel } = await supabase.from('tramites').select('id').eq('eliminado', true).lt('eliminado_en', limite);
    if (tramsDel?.length) {
      const tramIds = tramsDel.map(t => t.id);
      const { data: tramTareas } = await supabase.from('tareas').select('id').in('tramite_id', tramIds);
      if (tramTareas?.length) {
        await supabase.from('tareas_notas').delete().in('tarea_id', tramTareas.map(t => t.id));
        await supabase.from('tareas').delete().in('tramite_id', tramIds);
      }
      await supabase.from('tramites_notas').delete().in('tramite_id', tramIds);
      await supabase.from('agenda').delete().in('tramite_id', tramIds);
      await supabase.from('tramites').delete().in('id', tramIds);
    }

    // Tablas simples
    await supabase.from('clientes').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('deudas').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('gastos').delete().eq('eliminado', true).lt('eliminado_en', limite);
    await supabase.from('ingresos').delete().eq('eliminado', true).lt('eliminado_en', limite);

    const { data: asigDel } = await supabase.from('asignaciones').select('id').eq('eliminado', true).lt('eliminado_en', limite);
    if (asigDel?.length) {
      await supabase.from('asignacion_items').delete().in('asignacion_id', asigDel.map(a => a.id));
      await supabase.from('asignaciones').delete().in('id', asigDel.map(a => a.id));
    }
  }, []);

  const fetchTodo = useCallback(async () => {
    setLoading(true);
    await purgarAntiguos();

    const [
      { data: expedientes },
      { data: clientes },
      { data: tramites },
      { data: tareas },
      { data: agenda },
      { data: movimientos },
      { data: expNotas },
      { data: cliNotas },
      { data: treaNotas },
      { data: tramNotas },
      { data: agNotas },
      { data: pruebas },
      { data: deudas },
      { data: gastos },
      { data: ingresos },
      { data: asignaciones },
    ] = await Promise.all([
      supabase.from('expedientes').select('id, numero, caratula, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('clientes').select('id, nombre, apellido, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('tramites').select('id, nombre, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('tareas').select('id, titulo, expediente_id, tramite_id, eliminado_en, eliminado_con_id, expedientes(caratula), tramites(nombre)').eq('eliminado', true).is('eliminado_con_id', null).order('eliminado_en', { ascending: false }),
      supabase.from('agenda').select('id, tipo, fecha, descripcion, expediente_id, cliente_id, tramite_id, eliminado_en, eliminado_con_id, expedientes(caratula), clientes(nombre, apellido), tramites(nombre)').eq('eliminado', true).is('eliminado_con_id', null).order('eliminado_en', { ascending: false }),
      supabase.from('movimientos').select('id, descripcion, monto, fecha, eliminado_en, eliminado_con_id').eq('eliminado', true).is('eliminado_con_id', null).order('eliminado_en', { ascending: false }),
      supabase.from('expedientes_notas').select('id, contenido, eliminado_en, eliminado_con_id').eq('eliminado', true).is('eliminado_con_id', null).order('eliminado_en', { ascending: false }),
      supabase.from('clientes_notas').select('id, contenido, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('tareas_notas').select('id, contenido, eliminado_en, eliminado_con_id').eq('eliminado', true).is('eliminado_con_id', null).order('eliminado_en', { ascending: false }),
      supabase.from('tramites_notas').select('id, contenido, eliminado_en, eliminado_con_id').eq('eliminado', true).is('eliminado_con_id', null).order('eliminado_en', { ascending: false }),
      supabase.from('agenda_notas').select('id, contenido, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('expediente_pruebas').select('id, tipo, nombre, descripcion, eliminado_en, eliminado_con_id').eq('eliminado', true).is('eliminado_con_id', null).order('eliminado_en', { ascending: false }),
      supabase.from('deudas').select('id, concepto, monto_total, deudor_nombre, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('gastos').select('id, concepto, monto, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('ingresos').select('id, concepto, monto, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
      supabase.from('asignaciones').select('id, titulo, eliminado_en').eq('eliminado', true).order('eliminado_en', { ascending: false }),
    ]);

    const todasNotas = [
      ...(expNotas || []).map(n => ({ ...n, _tipo: 'nota_expediente' })),
      ...(cliNotas || []).map(n => ({ ...n, _tipo: 'nota_cliente' })),
      ...(treaNotas || []).map(n => ({ ...n, _tipo: 'nota_tarea' })),
      ...(tramNotas || []).map(n => ({ ...n, _tipo: 'nota_tramite' })),
      ...(agNotas || []).map(n => ({ ...n, _tipo: 'nota_agenda' })),
    ].sort((a, b) => (b.eliminado_en || '').localeCompare(a.eliminado_en || ''));

    setDatos({ expedientes, clientes, tramites, tareas, agenda, movimientos, notas: todasNotas, pruebas, deudas, gastos, ingresos, asignaciones });
    setLoading(false);
  }, [purgarAntiguos]);

  useEffect(() => { fetchTodo(); }, [fetchTodo]);

  async function handleRestaurar(tipo, item) {
    try {
      if (tipo === 'expediente') {
        await Promise.all([
          restaurarHijos('tareas', item.id),
          restaurarHijos('agenda', item.id),
          restaurarHijos('movimientos', item.id),
          restaurarHijos('expedientes_notas', item.id),
          restaurarHijos('expediente_pruebas', item.id),
        ]);
        await restaurar('expedientes', item.id);
      } else if (tipo === 'tramite') {
        await Promise.all([
          restaurarHijos('tareas', item.id),
          restaurarHijos('agenda', item.id),
          restaurarHijos('tramites_notas', item.id),
        ]);
        await restaurar('tramites', item.id);
      } else if (tipo === 'tarea') {
        await restaurarHijos('tareas_notas', item.id);
        await restaurar('tareas', item.id);
      } else if (tipo === 'prueba') {
        await restaurarHijos('prueba_eventos', item.id);
        await restaurar('expediente_pruebas', item.id);
      } else if (tipo === 'agenda') {
        await restaurar('agenda', item.id);
      } else if (tipo === 'movimiento') {
        await restaurar('movimientos', item.id);
      } else if (tipo === 'nota') {
        const tabla = { nota_expediente: 'expedientes_notas', nota_cliente: 'clientes_notas', nota_tarea: 'tareas_notas', nota_tramite: 'tramites_notas', nota_agenda: 'agenda_notas' }[item._tipo];
        if (tabla) await restaurar(tabla, item.id);
      } else if (tipo === 'cliente') {
        await restaurar('clientes', item.id);
      } else if (tipo === 'deuda') {
        await restaurar('deudas', item.id);
      } else if (tipo === 'gasto') {
        await restaurar('gastos', item.id);
      } else if (tipo === 'ingreso') {
        await restaurar('ingresos', item.id);
      } else if (tipo === 'asignacion') {
        await restaurar('asignaciones', item.id);
      }
      showToast('Elemento restaurado ✓');
      fetchTodo();
    } catch (e) {
      showToast('Error al restaurar', 'error');
    }
  }

  async function eliminarPermanente(tipo, item) {
    try {
      if (tipo === 'expediente') {
        const { data: tareas } = await supabase.from('tareas').select('id').in('expediente_id', [item.id]);
        if (tareas?.length) await supabase.from('tareas_notas').delete().in('tarea_id', tareas.map(t => t.id));
        const { data: pruebas } = await supabase.from('expediente_pruebas').select('id').eq('expediente_id', item.id);
        if (pruebas?.length) await supabase.from('prueba_eventos').delete().in('prueba_id', pruebas.map(p => p.id));
        await supabase.from('expediente_pruebas').delete().eq('expediente_id', item.id);
        await supabase.from('expedientes_notas').delete().eq('expediente_id', item.id);
        await supabase.from('movimientos').delete().eq('expediente_id', item.id);
        await supabase.from('tareas').delete().eq('expediente_id', item.id);
        await supabase.from('agenda').delete().eq('expediente_id', item.id);
        await supabase.from('expedientes').delete().eq('id', item.id);
      } else if (tipo === 'tramite') {
        const { data: tareas } = await supabase.from('tareas').select('id').eq('tramite_id', item.id);
        if (tareas?.length) await supabase.from('tareas_notas').delete().in('tarea_id', tareas.map(t => t.id));
        await supabase.from('tramites_notas').delete().eq('tramite_id', item.id);
        await supabase.from('tareas').delete().eq('tramite_id', item.id);
        await supabase.from('agenda').delete().eq('tramite_id', item.id);
        await supabase.from('tramites').delete().eq('id', item.id);
      } else if (tipo === 'tarea') {
        await supabase.from('tareas_notas').delete().eq('tarea_id', item.id);
        await supabase.from('tareas').delete().eq('id', item.id);
      } else if (tipo === 'prueba') {
        await supabase.from('prueba_eventos').delete().eq('prueba_id', item.id);
        await supabase.from('expediente_pruebas').delete().eq('id', item.id);
      } else if (tipo === 'agenda') {
        await supabase.from('agenda').delete().eq('id', item.id);
      } else if (tipo === 'movimiento') {
        await supabase.from('movimientos').delete().eq('id', item.id);
      } else if (tipo === 'nota') {
        const tabla = { nota_expediente: 'expedientes_notas', nota_cliente: 'clientes_notas', nota_tarea: 'tareas_notas', nota_tramite: 'tramites_notas', nota_agenda: 'agenda_notas' }[item._tipo];
        if (tabla) await supabase.from(tabla).delete().eq('id', item.id);
      } else if (tipo === 'cliente') {
        await supabase.from('clientes_notas').delete().eq('cliente_id', item.id);
        await supabase.from('clientes').delete().eq('id', item.id);
      } else if (tipo === 'deuda') {
        await supabase.from('deudas').delete().eq('id', item.id);
      } else if (tipo === 'gasto') {
        await supabase.from('gastos').delete().eq('id', item.id);
      } else if (tipo === 'ingreso') {
        await supabase.from('ingresos').delete().eq('id', item.id);
      } else if (tipo === 'asignacion') {
        await supabase.from('asignacion_items').delete().eq('asignacion_id', item.id);
        await supabase.from('asignaciones').delete().eq('id', item.id);
      }
      showToast('Eliminado permanentemente', 'error');
      fetchTodo();
    } catch (e) {
      showToast('Error al eliminar', 'error');
    }
  }

  function confirmarEliminar(tipo, item) {
    setConfirm({
      mensaje: '¿Eliminar permanentemente? Esta acción no se puede deshacer.',
      onConfirmar: () => { setConfirm(null); eliminarPermanente(tipo, item); }
    });
  }

  const total = Object.values(datos).reduce((s, arr) => s + (arr?.length || 0), 0);

  const tipoNota = (t) => ({ nota_expediente: 'Expediente', nota_cliente: 'Cliente', nota_tarea: 'Tarea', nota_tramite: 'Trámite', nota_agenda: 'Agenda' }[t] || '');

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Sistema</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Papelera de reciclaje</div>
        <div style={{ fontSize: 13, color: 'var(--text4)' }}>
          Los elementos se eliminan permanentemente a los {DIAS_PURGA} días de ser descartados.
          {total > 0 && <span style={{ marginLeft: 8, fontWeight: 600, color: 'var(--text3)' }}>{total} elemento{total !== 1 ? 's' : ''} en papelera.</span>}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text4)', fontSize: 13 }}>Cargando papelera…</div>
      ) : total === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text4)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)' }}>La papelera está vacía</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Todos los elementos eliminados aparecerán aquí.</div>
        </div>
      ) : (
        <>
          <SeccionPapelera
            titulo="Expedientes"
            items={datos.expedientes}
            onRestaurar={i => handleRestaurar('expediente', i)}
            onEliminar={i => confirmarEliminar('expediente', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i.numero ? `Exp. ${i.numero} — ` : ''}{i.caratula || 'Sin carátula'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>Eliminado el {formatFecha(i.eliminado_en)} · Se restauran tareas, audiencias, notas, movimientos y pruebas</div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Clientes"
            items={datos.clientes}
            onRestaurar={i => handleRestaurar('cliente', i)}
            onEliminar={i => confirmarEliminar('cliente', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{i.apellido} {i.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>Eliminado el {formatFecha(i.eliminado_en)}</div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Trámites extrajudiciales"
            items={datos.tramites}
            onRestaurar={i => handleRestaurar('tramite', i)}
            onEliminar={i => confirmarEliminar('tramite', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>Eliminado el {formatFecha(i.eliminado_en)} · Se restauran tareas, audiencias y notas</div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Tareas"
            items={datos.tareas}
            onRestaurar={i => handleRestaurar('tarea', i)}
            onEliminar={i => confirmarEliminar('tarea', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.titulo || 'Sin título'}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>
                  {i.expedientes?.caratula && <span>{i.expedientes.caratula} · </span>}
                  {i.tramites?.nombre && <span>{i.tramites.nombre} · </span>}
                  Eliminado el {formatFecha(i.eliminado_en)}
                </div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Agenda y audiencias"
            items={datos.agenda}
            onRestaurar={i => handleRestaurar('agenda', i)}
            onEliminar={i => confirmarEliminar('agenda', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{i.tipo || 'Evento'} {i.fecha ? `— ${formatFecha(i.fecha)}` : ''}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>
                  {i.expedientes?.caratula && <span>{i.expedientes.caratula} · </span>}
                  {i.tramites?.nombre && <span>{i.tramites.nombre} · </span>}
                  {i.clientes && <span>{i.clientes.apellido} {i.clientes.nombre} · </span>}
                  Eliminado el {formatFecha(i.eliminado_en)}
                </div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Movimientos"
            items={datos.movimientos}
            onRestaurar={i => handleRestaurar('movimiento', i)}
            onEliminar={i => confirmarEliminar('movimiento', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.descripcion || 'Sin descripción'}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>
                  {i.monto && <span>${i.monto.toLocaleString('es-AR')} · </span>}
                  Eliminado el {formatFecha(i.eliminado_en)}
                </div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Pruebas"
            items={datos.pruebas}
            onRestaurar={i => handleRestaurar('prueba', i)}
            onEliminar={i => confirmarEliminar('prueba', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{i.nombre || i.descripcion || i.tipo || 'Prueba'}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>Tipo: {i.tipo} · Eliminado el {formatFecha(i.eliminado_en)}</div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Notas"
            items={datos.notas}
            onRestaurar={i => handleRestaurar('nota', i)}
            onEliminar={i => confirmarEliminar('nota', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text4)', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '1px 6px', marginRight: 6 }}>{tipoNota(i._tipo)}</span>
                  {i.contenido || '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>Eliminado el {formatFecha(i.eliminado_en)}</div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Contabilidad"
            items={[
              ...(datos.deudas || []).map(d => ({ ...d, _tipo: 'deuda', _label: `Deuda — ${d.deudor_nombre || d.concepto || '—'}`, _monto: d.monto_total })),
              ...(datos.gastos || []).map(g => ({ ...g, _tipo: 'gasto', _label: `Gasto — ${g.concepto || '—'}`, _monto: g.monto })),
              ...(datos.ingresos || []).map(i => ({ ...i, _tipo: 'ingreso', _label: `Ingreso — ${i.concepto || '—'}`, _monto: i.monto })),
            ].sort((a, b) => (b.eliminado_en || '').localeCompare(a.eliminado_en || ''))}
            onRestaurar={i => handleRestaurar(i._tipo, i)}
            onEliminar={i => confirmarEliminar(i._tipo, i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i._label}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>
                  {i._monto && <span>${i._monto.toLocaleString('es-AR')} · </span>}
                  Eliminado el {formatFecha(i.eliminado_en)}
                </div>
              </>
            )}
          />
          <SeccionPapelera
            titulo="Asignaciones"
            items={datos.asignaciones}
            onRestaurar={i => handleRestaurar('asignacion', i)}
            onEliminar={i => confirmarEliminar('asignacion', i)}
            renderItem={i => (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.titulo || 'Sin título'}</div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>Eliminado el {formatFecha(i.eliminado_en)}</div>
              </>
            )}
          />
        </>
      )}

      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '28px 28px 22px', maxWidth: 360, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Confirmar eliminación</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 22 }}>{confirm.mensaje}</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirm(null)}>Cancelar</button>
              <button className="btn" style={{ background: '#dc2626', color: 'white', border: 'none' }} onClick={confirm.onConfirmar}>Eliminar permanentemente</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: toast.tipo === 'error' ? '#fef2f2' : '#f0fdf4', color: toast.tipo === 'error' ? '#b91c1c' : '#15803d', border: `0.5px solid ${toast.tipo === 'error' ? '#fecaca' : '#bbf7d0'}`, padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
